'use client'

import { motion } from "framer-motion"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import {
  Server,
  Users,
  Shield,
  MessageCircle,
  Globe,
  ArrowRight,
  ExternalLink,
  BookOpen,
  HelpCircle,
} from "lucide-react"
import Link from "next/link"

export function HeroSection() {
  return (
    <div className="relative container mx-auto px-4 py-24 lg:py-32">
      <div className="grid lg:grid-cols-2 gap-12 items-center">
        <motion.div 
          className="space-y-8"
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="space-y-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <Badge variant="secondary" className="w-fit shimmer glass">
                <Globe className="mr-2 h-4 w-4" />
                Global IRC Network
              </Badge>
            </motion.div>
            
            <motion.h1 
              className="text-4xl lg:text-7xl font-bold tracking-tight leading-tight"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              Welcome to the{" "}
              <span className="gradient-text">
                Undernet
              </span>
              <br />
              IRC Network
            </motion.h1>
            
            <motion.p 
              className="text-xl lg:text-2xl text-muted-foreground max-w-2xl leading-relaxed"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              One of the largest IRC networks in the world, with approximately 40 servers connecting
              people worldwide.
            </motion.p>
          </div>

          <motion.div 
            className="flex flex-col sm:flex-row gap-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
          >
            <Button size="lg" className="btn-gradient button-hover group" asChild>
              <a href="https://chat.undernet.org" target="_blank" rel="noopener noreferrer">
                <MessageCircle className="mr-2 h-5 w-5 group-hover:animate-pulse" />
                Start Chatting
                <ExternalLink className="ml-2 h-4 w-4 group-hover:rotate-12 transition-transform" />
              </a>
            </Button>
            <Button variant="outline" size="lg" className="glass button-hover group" asChild>
              <Link href="/servers">
                <Server className="mr-2 h-5 w-5 group-hover:animate-pulse" />
                View Servers
                <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </Button>
          </motion.div>
        </motion.div>

        <motion.div 
          className="hidden lg:block"
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          <div className="grid grid-cols-2 gap-6">
            <motion.div
              whileHover={{ scale: 1.05 }}
              transition={{ type: "spring", stiffness: 400, damping: 10 }}
            >
              <Card className="gradient-bg-card card-hover-subtle">
                <CardHeader className="pb-3">
                  <CardTitle className="text-3xl font-bold gradient-text">~40</CardTitle>
                  <CardDescription className="text-base">Active Servers</CardDescription>
                </CardHeader>
              </Card>
            </motion.div>
            
            <motion.div
              whileHover={{ scale: 1.05 }}
              transition={{ type: "spring", stiffness: 400, damping: 10 }}
            >
              <Card className="gradient-bg-card card-hover-subtle">
                <CardHeader className="pb-3">
                  <CardTitle className="text-3xl font-bold gradient-text-emerald">24/7</CardTitle>
                  <CardDescription className="text-base">Network Uptime</CardDescription>
                </CardHeader>
              </Card>
            </motion.div>
            
            <motion.div
              whileHover={{ scale: 1.05 }}
              transition={{ type: "spring", stiffness: 400, damping: 10 }}
            >
              <Card className="gradient-bg-card card-hover-subtle">
                <CardHeader className="pb-3">
                  <CardTitle className="text-3xl font-bold gradient-text">Global</CardTitle>
                  <CardDescription className="text-base">Worldwide Coverage</CardDescription>
                </CardHeader>
              </Card>
            </motion.div>
            
            <motion.div
              whileHover={{ scale: 1.05 }}
              transition={{ type: "spring", stiffness: 400, damping: 10 }}
            >
              <Card className="gradient-bg-card card-hover-subtle">
                <CardHeader className="pb-3">
                  <CardTitle className="text-3xl font-bold gradient-text">Free</CardTitle>
                  <CardDescription className="text-base">Always Free</CardDescription>
                </CardHeader>
              </Card>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </div>
  )
}

export function FeaturesSection() {
  return (
    <section className="py-24 relative overflow-hidden">
      <div className="absolute inset-0 theme-bg-light" />
      
      <div className="container mx-auto px-4 relative">
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="text-3xl lg:text-5xl font-bold mb-6">
            Why Choose{" "}
            <span className="gradient-text">Undernet</span>?
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            Discover what makes Undernet one of the most trusted IRC networks worldwide.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true }}
            whileHover={{ y: -5 }}
          >
            <Card className="border-0 gradient-bg-card card-hover-subtle h-full">
              <CardHeader className="pb-6">
                <motion.div 
                  className="w-16 h-16 rounded-2xl theme-btn-gradient flex items-center justify-center mb-6 shadow-lg"
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  transition={{ type: "spring", stiffness: 400, damping: 10 }}
                >
                  <Server className="h-8 w-8 text-white" />
                </motion.div>
                <CardTitle className="text-xl mb-3">Reliable Infrastructure</CardTitle>
                <CardDescription className="text-base leading-relaxed">
                  Robust server network with high availability and redundancy across multiple continents.
                </CardDescription>
              </CardHeader>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            whileHover={{ y: -5 }}
          >
            <Card className="border-0 gradient-bg-card card-hover-subtle h-full">
              <CardHeader className="pb-6">
                <motion.div 
                  className="w-16 h-16 rounded-2xl theme-btn-gradient flex items-center justify-center mb-6 shadow-lg"
                  whileHover={{ scale: 1.1, rotate: -5 }}
                  transition={{ type: "spring", stiffness: 400, damping: 10 }}
                >
                  <Shield className="h-8 w-8 text-white" />
                </motion.div>
                <CardTitle className="text-xl mb-3">Advanced Services</CardTitle>
                <CardDescription className="text-base leading-relaxed">
                  Channel registration, NickServ, ChanServ, and other services to protect your identity and channels.
                </CardDescription>
              </CardHeader>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            viewport={{ once: true }}
            whileHover={{ y: -5 }}
          >
            <Card className="border-0 gradient-bg-card card-hover-subtle h-full">
              <CardHeader className="pb-6">
                <motion.div 
                  className="w-16 h-16 rounded-2xl theme-btn-gradient flex items-center justify-center mb-6 shadow-lg"
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  transition={{ type: "spring", stiffness: 400, damping: 10 }}
                >
                  <Users className="h-8 w-8 text-white" />
                </motion.div>
                <CardTitle className="text-xl mb-3">Active Community</CardTitle>
                <CardDescription className="text-base leading-relaxed">
                  Join thousands of users in channels covering every topic imaginable, from tech to hobbies.
                </CardDescription>
              </CardHeader>
            </Card>
          </motion.div>
        </div>
      </div>
    </section>
  )
}

export function QuickLinksSection() {
  return (
    <section className="py-24 relative">
      <div className="absolute inset-0 theme-bg-hero" />
      
      <div className="container mx-auto px-4 relative">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="text-3xl lg:text-4xl font-bold mb-4">
            Get Started with{" "}
            <span className="gradient-text">Undernet</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Quick access to everything you need to connect and participate in the Undernet community.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true }}
          >
            <Card className="group gradient-bg-card card-hover h-full">
              <CardHeader className="pb-4">
                <motion.div
                  whileHover={{ scale: 1.2, rotate: 10 }}
                  transition={{ type: "spring", stiffness: 400, damping: 10 }}
                >
                  <Globe className="h-10 w-10 text-primary mb-3" />
                </motion.div>
                <CardTitle className="text-lg mb-2">Server List</CardTitle>
                <CardDescription className="text-sm">
                  Browse our current list of servers and find the best connection for your location.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button variant="ghost" className="w-full justify-between group-hover:bg-muted group-hover:text-primary transition-colors" asChild>
                  <Link href="/servers">
                    <span>View Servers</span>
                    <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </Button>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <Card className="group gradient-bg-card card-hover h-full">
              <CardHeader className="pb-4">
                <motion.div
                  whileHover={{ scale: 1.2, rotate: -10 }}
                  transition={{ type: "spring", stiffness: 400, damping: 10 }}
                >
                  <HelpCircle className="h-10 w-10 text-accent mb-3" />
                </motion.div>
                <CardTitle className="text-lg mb-2">Need Help?</CardTitle>
                <CardDescription className="text-sm">
                  Get started with our comprehensive help section and FAQ.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button variant="ghost" className="w-full justify-between group-hover:bg-muted group-hover:text-accent transition-colors" asChild>
                  <Link href="/help">
                    <span>Get Help</span>
                    <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </Button>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            viewport={{ once: true }}
          >
            <Card className="group gradient-bg-card card-hover h-full">
              <CardHeader className="pb-4">
                <motion.div
                  whileHover={{ scale: 1.2, rotate: 10 }}
                  transition={{ type: "spring", stiffness: 400, damping: 10 }}
                >
                  <BookOpen className="h-10 w-10 text-accent mb-3" />
                </motion.div>
                <CardTitle className="text-lg mb-2">Documentation</CardTitle>
                <CardDescription className="text-sm">
                  Explore our detailed documentation and learn about IRC and Undernet.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button variant="ghost" className="w-full justify-between group-hover:bg-muted group-hover:text-accent transition-colors" asChild>
                  <Link href="/docs">
                    <span>Read Docs</span>
                    <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </Button>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            viewport={{ once: true }}
          >
            <Card className="group gradient-bg-card card-hover h-full">
              <CardHeader className="pb-4">
                <motion.div
                  whileHover={{ scale: 1.2, rotate: -10 }}
                  transition={{ type: "spring", stiffness: 400, damping: 10 }}
                >
                  <MessageCircle className="h-10 w-10 text-primary mb-3" />
                </motion.div>
                <CardTitle className="text-lg mb-2">Webchat</CardTitle>
                <CardDescription className="text-sm">
                  Connect instantly through your browser with our web-based IRC client.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button variant="ghost" className="w-full justify-between group-hover:bg-muted group-hover:text-primary transition-colors" asChild>
                  <a href="https://chat.undernet.org" target="_blank" rel="noopener noreferrer">
                    <span>Open Webchat</span>
                    <ExternalLink className="h-4 w-4 group-hover:translate-x-1 group-hover:rotate-12 transition-transform" />
                  </a>
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </section>
  )
}

export function CharterSection() {
  return (
    <section className="py-24 relative overflow-hidden">
      <div className="absolute inset-0 theme-bg-light" />
      
      <div className="container mx-auto px-4 relative">
        <motion.div 
          className="max-w-4xl mx-auto text-center"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <motion.h2 
            className="text-3xl lg:text-5xl font-bold mb-6"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <span className="gradient-text">Undernet</span> Charter
          </motion.h2>
          
          <motion.p 
            className="text-lg lg:text-xl text-muted-foreground mb-8 leading-relaxed"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            viewport={{ once: true }}
          >
            The Undernet Charter provides the foundation for our network to function. Built on 25+ years of evolution,
            it incorporates the ideas and experiences of volunteers who have built and maintained this system.
          </motion.p>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            viewport={{ once: true }}
          >
            <Button variant="outline" size="lg" className="glass button-hover group" asChild>
              <Link href="https://charter.undernet.org">
                <BookOpen className="mr-2 h-5 w-5 group-hover:animate-pulse" />
                Read the Charter
                <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </Button>
          </motion.div>
        </motion.div>
      </div>
    </section>
  )
}